import { useState, useCallback, useRef } from 'react'
import {
  useCartStore,
  buildSyntheticSizeGroup,
  getLowestVariantPrice,
  variantKeyFromOptionId,
  SYNTHETIC_SIZE_GROUP_ID,
} from '@restaurant/core'
import type { CartItem } from '@restaurant/core'
import { useProductCustomization } from './useProductCustomization'
import type { OptionGroupData } from './useProductCustomization'

export interface UseEditCartItemConfig {
  onSave?: () => void
}

export interface UseEditCartItemReturn {
  editingCartItem: CartItem | null
  optionGroups: OptionGroupData[]
  unitPrice: number
  formattedPrice: string
  formattedBasePrice: string
  specialInstructions: string
  setSpecialInstructions: (text: string) => void
  onOptionChange: (groupId: string, optionId: string, selected: boolean, isRadio: boolean) => void
  openEdit: (itemId: string) => void
  saveEdit: () => void
  cancelEdit: () => void
}

export function useEditCartItem(config?: UseEditCartItemConfig): UseEditCartItemReturn {
  const [editingCartItem, setEditingCartItem] = useState<CartItem | null>(null)

  const customization = useProductCustomization(null)
  const customizationRef = useRef(customization)
  customizationRef.current = customization

  const configRef = useRef(config)
  configRef.current = config

  const openEdit = useCallback((itemId: string) => {
    const cartItem = useCartStore.getState().items.find((i) => i.id === itemId)
    if (!cartItem) return

    const initialSelections: Record<string, Set<string>> = {}
    for (const sel of cartItem.customizations) {
      let selectionSet = initialSelections[sel.customizationItemId]
      if (!selectionSet) {
        selectionSet = new Set()
        initialSelections[sel.customizationItemId] = selectionSet
      }
      selectionSet.add(sel.selectedOptionId)
    }

    const sizeGroup = buildSyntheticSizeGroup(cartItem.menuItem)
    if (sizeGroup && cartItem.selectedPriceVariant) {
      const matchedOption = sizeGroup.options.find(
        (opt) => variantKeyFromOptionId(opt.id) === cartItem.selectedPriceVariant,
      )
      if (matchedOption) {
        initialSelections[SYNTHETIC_SIZE_GROUP_ID] = new Set([matchedOption.id])
      }
    }

    customizationRef.current.reset(cartItem.menuItem, initialSelections, cartItem.specialInstructions)
    setEditingCartItem(cartItem)
  }, [])

  const saveEdit = useCallback(() => {
    const item = editingCartItem
    if (!item) return

    const c = customizationRef.current
    useCartStore.getState().updateItem(item.id, {
      customizations: c.customizationsForCart,
      selectedPriceVariant: c.selectedPriceVariant,
      totalPrice: c.unitPrice * item.quantity,
      specialInstructions: c.specialInstructions || undefined,
    })

    configRef.current?.onSave?.()
    setEditingCartItem(null)
  }, [editingCartItem])

  const cancelEdit = useCallback(() => {
    setEditingCartItem(null)
  }, [])

  const basePrice = editingCartItem
    ? getLowestVariantPrice(editingCartItem.menuItem)
    : 0

  return {
    editingCartItem,
    optionGroups: customization.optionGroups,
    unitPrice: customization.unitPrice,
    formattedPrice: `$${customization.unitPrice.toFixed(2)}`,
    formattedBasePrice: `$${basePrice.toFixed(2)}`,
    specialInstructions: customization.specialInstructions,
    setSpecialInstructions: customization.setSpecialInstructions,
    onOptionChange: customization.onOptionChange,
    openEdit,
    saveEdit,
    cancelEdit,
  }
}
